import { YPaymentAmount } from './amount';
import { $i18n, yInterpolate } from './i18n';

export namespace $format {
    const _locale = {
        decimal: '.',
        thousands: ',',
        grouping: [3],
        currency: ['$', ''],
        dateTime: '%a %b %e %X %Y',
        date: '%m/%d/%Y',
        time: '%H:%M:%S',
        periods: ['AM', 'PM'],
        eras: ['BC', 'AD']
    };

    export function padNumber(value: number, size: number, trim?: boolean, zero = '0') {
        let sign = '';
        if(value < 0) {
            value = -value;
            sign = '-';
        }
        let num = '' + value;
        while(num.length < size) {
            num = zero + num;
        }
        if(trim && num.length > size) {
            num = num.substr(num.length - size);
        }
        return sign + num;
    }

    export function maxNumber(value: number, size: number) {
        let str = value.toFixed(size);

        while(size && str.endsWith('0'))
            str = str.substr(0, str.length - 1);

        if(str.endsWith('.'))
            str = str.substr(0, str.length - 1);

        return str;
    }

    export function fileSize(value: number) {
        if(value < 1000)
            return Math.floor(value) + ' bytes';
        else if(value < 99500)
            return (value / 1000).toFixed(1) + ' KB';
        else if(value < 999500)
            return (value / 1000).toFixed(0) + ' KB';
        else if(value < 99500000)
            return (value / 1000000).toFixed(1) + ' MB';
        else if(value < 999500000)
            return (value / 1000000).toFixed(0) + ' MB';
        else if(value < 99500000000)
            return (value / 1000000000).toFixed(1) + ' GB';
        else
            return (value / 1000000000).toFixed(0) + ' GB';
    }

    export function currency(a: YPaymentAmount) {
        let value = (a.value / 100).toFixed(a.value % 100 == 0 ? 0 : 2);
        let parts = value.split('.');
        if(parts[0].length > 3) {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
        }

        return yInterpolate($i18n.y.currency_format[a.currency] || '$1$', parts.join('.'));
    }

    export function currencyFull(a: YPaymentAmount) {
        let value = (a.value / 100).toFixed(2);
        let parts = value.split('.');
        if(parts[0].length > 3) {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
        }

        return yInterpolate($i18n.y.currency_format[a.currency] || '$1$', parts.join('.'));
    }

    export function number(n: number, _f: string) {
        let rem = n.toFixed(0);
        let res = '';

        while(rem.length) {
            if(rem.length > 3) {
                res = ' ' + rem.slice(-3) + res;
                rem = rem.slice(0, -3);
            } else {
                res = rem + res;
                break;
            }
        }

        return res;
    }

    export function duration(v: number | undefined) {
        if(v == null) return '-';
        let sign = '';
        if(v < 0) {
            sign = '-';
            v = -v;
        } else if(v == 0) {
            return '0s';
        }

        if(v >= 60) {
            v = Math.floor(v);
            const parts = [];
            if(v >= 86400) {
                parts.push(Math.floor(v / 86400) + 'd');
                v = v % 86400;
            }
            if(v >= 3600) {
                parts.push(Math.floor(v / 3600) + 'h');
                v = v % 3600;
            }
            if(v >= 60) {
                parts.push(Math.floor(v / 60) + 'm');
                v = v % 60;
            }
            if(v > 0) {
                parts.push(v + 's');
            }
            return sign + parts.join(' ');
        } else if(v == Math.floor(v)) {
            return sign + v + 's';
        } else if(v >= 10) {
            return sign + v.toFixed(1) + 's';
        } else if(v >= 1) {
            const num = v.toFixed(2);
            if(num.length > 4)
                return sign + '10.0s';
            return sign + num + 's';
        }
        v *= 1000;
        if(v == Math.floor(v)) {
            return sign + v + 'ms';
        } else if(v >= 100) {
            const num = v.toFixed(0);
            if(num.length > 3)
                return sign + '1.00s';
            return sign + num + 'ms';
        } else if(v >= 10) {
            const num = v.toFixed(1);
            if(num.length > 4)
                return sign + '100ms';
            return sign + num + 'ms';
        } else if(v >= 1) {
            const num = v.toFixed(2);
            if(num.length > 4)
                return sign + '10.0ms';
            return sign + num + 'ms';
        }
        v *= 1000;
        const num = v.toFixed(0);
        if(num.length > 3)
            return sign + '1.00ms';
        return sign + num + 'µs';
    }



























    const enum DateFormatComponentType { NUMBER, STRING, ERA, AMPM, WEEK, HOURS12, FN };

    type NativeDateFn = 'getUTCFullYear' | 'getUTCMonth' | 'y_getUTCQuarter' | 'getUTCDate' | 'getUTCDay' | 'getUTCHours' | 'y_getUTCHours12' | 'getUTCMinutes' |
        'getUTCSeconds' | 'y_getUTCWeek' | 'getUTCMilliseconds'

    interface DateNumberFormatComponent {
        type: DateFormatComponentType.NUMBER;
        fn: NativeDateFn;
        size: number;
        offset: number;
        trim?: boolean;
    }

    interface DateStringFormatComponent {
        type: DateFormatComponentType.STRING;
        fn: NativeDateFn;
        facet: 'days' | 'short_days' | 'months' | 'short_months';
    }

    interface DateFnFormatComponent {
        type: DateFormatComponentType.FN;
        fn: string;
    }

    interface DateSpecialFormatComponent {
        type: DateFormatComponentType.ERA | DateFormatComponentType.AMPM;
    }

    type DateFormatComponent = DateNumberFormatComponent | DateFnFormatComponent | DateStringFormatComponent | DateSpecialFormatComponent;

    const _date_format_components: { [f: string]: DateFormatComponent } = {
        yyyy: { type: DateFormatComponentType.NUMBER, fn: 'getUTCFullYear', size: 4, offset: 0 },
        yy: { type: DateFormatComponentType.NUMBER, fn: 'getUTCFullYear', size: 2, offset: 0, trim: true },
        y: { type: DateFormatComponentType.NUMBER, fn: 'getUTCFullYear', size: 1, offset: 0, trim: false },
        MMMM: { type: DateFormatComponentType.STRING, fn: 'getUTCMonth', facet: 'months' },
        MMM: { type: DateFormatComponentType.STRING, fn: 'getUTCMonth', facet: 'short_months' },
        MM: { type: DateFormatComponentType.NUMBER, fn: 'getUTCMonth', size: 2, offset: 1 },
        M: { type: DateFormatComponentType.NUMBER, fn: 'getUTCMonth', size: 1, offset: 1 },
        QQ: { type: DateFormatComponentType.FN, fn: 'y_getUTCQuarter' },
        LLLL: { type: DateFormatComponentType.STRING, fn: 'getUTCMonth', facet: 'months' }, //should be standalone!!
        dd: { type: DateFormatComponentType.NUMBER, fn: 'getUTCDate', size: 2, offset: 0 },
        d: { type: DateFormatComponentType.NUMBER, fn: 'getUTCDate', size: 1, offset: 0 },
        HH: { type: DateFormatComponentType.NUMBER, fn: 'getUTCHours', size: 2, offset: 0 },
        H: { type: DateFormatComponentType.NUMBER, fn: 'getUTCHours', size: 1, offset: 0 },
        hh: { type: DateFormatComponentType.NUMBER, fn: 'y_getUTCHours12', size: 2, offset: 0 },
        h: { type: DateFormatComponentType.NUMBER, fn: 'y_getUTCHours12', size: 1, offset: 0 },
        mm: { type: DateFormatComponentType.NUMBER, fn: 'getUTCMinutes', size: 2, offset: 0 },
        m: { type: DateFormatComponentType.NUMBER, fn: 'getUTCMinutes', size: 1, offset: 0 },
        ss: { type: DateFormatComponentType.NUMBER, fn: 'getUTCSeconds', size: 2, offset: 0 },
        s: { type: DateFormatComponentType.NUMBER, fn: 'getUTCSeconds', size: 1, offset: 0 },
        sss: { type: DateFormatComponentType.NUMBER, fn: 'getUTCMilliseconds', size: 3, offset: 0 },
        EEEE: { type: DateFormatComponentType.STRING, fn: 'getUTCDay', facet: 'days' },
        EEE: { type: DateFormatComponentType.STRING, fn: 'getUTCDay', facet: 'short_days' },
        a: { type: DateFormatComponentType.AMPM },
        ww: { type: DateFormatComponentType.NUMBER, fn: 'y_getUTCWeek', size: 2, offset: 0 },
        w: { type: DateFormatComponentType.NUMBER, fn: 'y_getUTCWeek', size: 1, offset: 0 },
        R: { type: DateFormatComponentType.FN, fn: 'y_getAgoString' },
        RR: { type: DateFormatComponentType.FN, fn: 'y_getAgoDateString' },
        // Z: timeZoneGetter,
        G: { type: DateFormatComponentType.ERA },
    }

    type DateParsedFormat = (string | DateFormatComponent)[];

    const _new_parsed_date_formats: { [f: string]: DateParsedFormat } = {};

    const _DATE_FORMAT_PART = /([^yMQLdHhmsaZEwG']+)|('[^']*')|(E+|y+|M+|Q+|L+|d+|H+|h+|m+|s+|a|Z|G+|w+)/g;

    function _parseDateFormat(f: string): DateParsedFormat {
        const raw = f.match(_DATE_FORMAT_PART);

        if(!raw) throw new Error('Invalid date format');

        const parts: DateParsedFormat = [];

        const enum LastToken { None, Str, Quoted }
        let last = LastToken.None;

        for(let r of raw) {
            const fn = _date_format_components[r];
            if(fn) {
                parts.push(fn);
                last = LastToken.None;
            } else if(r.charAt(0) === '\'') {
                r = r.slice(1, -1);
                if(last == LastToken.Quoted)
                    parts[parts.length - 1] += '\'' + r;
                else if(last == LastToken.Str)
                    parts[parts.length - 1] += r;
                else
                    parts.push(r);
                last = LastToken.Quoted;
            } else {
                if(last != LastToken.None)
                    parts[parts.length - 1] += r;
                else
                    parts.push(r);
                last = LastToken.Str;
            }
        }

        return parts;
    }

    export function timeUTC(d: Date, f: string): string {
        let parsed = _new_parsed_date_formats[f];
        if(!parsed) {
            parsed = _parseDateFormat(f);
            _new_parsed_date_formats[f] = parsed;
        }

        let str = '';
        for(const p of parsed) {
            if(typeof p === 'string') {
                str += p;
            } else {
                switch(p.type) {
                    case DateFormatComponentType.FN:
                        str += (d as any)[p.fn]();
                        break;
                    case DateFormatComponentType.NUMBER:
                        str += padNumber((d as any)[p.fn]() + p.offset, p.size, p.trim);
                        break;
                    case DateFormatComponentType.STRING:
                        str += $i18n.y[p.facet][(d as any)[p.fn]()];
                        break;
                    case DateFormatComponentType.ERA:
                        str += _locale.eras[d.getUTCFullYear() <= 0 ? 0 : 1];
                        break;
                    case DateFormatComponentType.AMPM:
                        str += _locale.periods[d.getUTCHours() < 12 ? 0 : 1];
                        break;
                }
            }
        }
        return str;
    }

    export function timeLocal(d: Date, f: string): string {
        return timeUTC(new Date(d.getTime() - 60000 * d.getTimezoneOffset()), f);
    }
}
